import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Stethoscope, Activity, Clock, Calendar, Filter, TrendingUp, Download } from 'lucide-react';
import axios from 'axios';
import { ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, Cell } from 'recharts';
import ExcelJS from 'exceljs';
import { API_URL } from '../config';

// Vibrant, distinct palette inspired by the reference "Traffic Sources" chart
const STATUS_COLORS = {
    'NO ANSWER': '#ef4444',
    'VOICEMAIL': '#f97316',
    'FOLLOW UP': '#eab308',
    'CALLBACK': '#1e40af',
    'SEND EMAIL': '#6366f1',
    'SEND FAX': '#06b6d4',
    'PROCESS': '#14b8a6',
    'COMPLETED': '#22c55e',
    'PENDING': '#94a3b8',
    'APPOINTMENT FIRST': '#0ea5e9',
    'CALL BACK': '#8b5cf6',
    'PATIENT CALL': '#fb923c',
    'PROCESSING': '#10b981',
    'REQUEST ELECTRONICALLY': '#ec4899',
    'DATA NOT FOUND': '#f59e0b',
    'ADDITIONAL INFO': '#3b82f6',
    "DON'T NEED": '#475569',
    'INVALID': '#dc2626',
    'DISCONTINUED': '#7f1d1d',
    'OTHERS': '#6b7280'
};

// Status rows in display order, with row & header background colors matching the spreadsheet reference.
// `fill` is the ARGB hex for Excel exports (matches Tailwind class).
// `dark` indicates the background is dark, so font color in Excel should be white.
const STATUS_ROWS = [
    { key: 'APPOINTMENT FIRST',      label: 'APPOINTMENT FIRST',      bg: 'bg-sky-100',     text: 'text-gray-800', headBg: 'bg-sky-100',                fill: 'FFE0F2FE' },
    { key: 'CALL BACK',              label: 'CALL BACK',              bg: 'bg-purple-100',  text: 'text-gray-800', headBg: 'bg-purple-100',             fill: 'FFF3E8FF' },
    { key: 'PATIENT CALL',           label: 'PATIENT CALL',           bg: 'bg-orange-100',  text: 'text-gray-800', headBg: 'bg-orange-100',             fill: 'FFFFEDD5' },
    { key: 'PROCESSING',             label: 'PROCESSING',             bg: 'bg-green-100',   text: 'text-gray-800', headBg: 'bg-green-100',              fill: 'FFDCFCE7' },
    { key: 'REQUEST ELECTRONICALLY', label: 'REQUEST ELECTRONICALLY', bg: 'bg-pink-100',    text: 'text-gray-800', headBg: 'bg-pink-100',               fill: 'FFFCE7F3' },
    { key: 'DATA NOT FOUND',         label: 'DATA NOT FOUND',         bg: 'bg-yellow-100',  text: 'text-gray-800', headBg: 'bg-yellow-100',             fill: 'FFFEF9C3' },
    { key: 'ADDITIONAL INFO',        label: 'ADDITIONAL INFO',        bg: 'bg-blue-100',    text: 'text-gray-800', headBg: 'bg-blue-100',               fill: 'FFDBEAFE' },
    { key: "DON'T NEED",             label: "DON'T NEED",             bg: 'bg-gray-700',    text: 'text-white',    headBg: 'bg-gray-700 text-white',    fill: 'FF374151', dark: true },
    { key: 'INVALID',                label: 'INVALID',                bg: 'bg-red-700',     text: 'text-white',    headBg: 'bg-red-700 text-white',     fill: 'FFB91C1C', dark: true },
    { key: 'DISCONTINUED',           label: 'DISCONTINUED',           bg: 'bg-red-900',     text: 'text-white',    headBg: 'bg-red-900 text-white',     fill: 'FF7F1D1D', dark: true },
    { key: 'OTHERS',                 label: 'OTHERS',                 bg: 'bg-blue-200',    text: 'text-gray-800', headBg: 'bg-blue-200',               fill: 'FFBFDBFE' },
];

const HEADER_FILL = 'FFBBF7D0'; // green-200 (Daily Progress header)
const NEUTRAL_FILL = 'FFF3F4F6'; // gray-100 (Month/Year/Total cells)
const TOTAL_FILL = 'FFFFFFFF';

// Trigger a browser download from an exceljs Workbook
const downloadWorkbook = async (wb, fileName) => {
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

const styleCellFill = (cell, argb, dark = false, bold = false) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb } };
    cell.font = { color: { argb: dark ? 'FFFFFFFF' : 'FF1F2937' }, bold };
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
    cell.border = {
        top:    { style: 'thin', color: { argb: 'FFD1D5DB' } },
        left:   { style: 'thin', color: { argb: 'FFD1D5DB' } },
        right:  { style: 'thin', color: { argb: 'FFD1D5DB' } },
        bottom: { style: 'thin', color: { argb: 'FFD1D5DB' } }
    };
};

// Build a per-month grid for the Monthly Progress table.
// Always returns the trailing 12 months ending at current month so empty months still appear.
const buildMonthlyRows = (rows) => {
    const map = new Map();
    (rows || []).forEach(r => {
        const ym = r.ym;
        if (!map.has(ym)) map.set(ym, { counts: {}, total: 0 });
        const entry = map.get(ym);
        const status = (r.status || '').toUpperCase();
        const c = Number(r.count) || 0;
        entry.counts[status] = (entry.counts[status] || 0) + c;
        entry.total += c;
    });

    if (map.size === 0) return [];

    // Find min and max dates from the dataset
    const yms = Array.from(map.keys()).sort();
    const minYm = yms[0];
    const maxYm = yms[yms.length - 1];

    const [minYear, minMonth] = minYm.split('-').map(Number);
    const [maxYear, maxMonth] = maxYm.split('-').map(Number);

    const out = [];
    const minDate = new Date(minYear, minMonth - 1, 1);
    const maxDate = new Date(maxYear, maxMonth - 1, 1);

    let currentDate = new Date(minDate);
    while (currentDate <= maxDate) {
        const ym = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
        const monthName = currentDate.toLocaleString('en-US', { month: 'long' });
        const year = currentDate.getFullYear();
        const entry = map.get(ym) || { counts: {}, total: 0 };
        out.push({ ym, monthName, year, counts: entry.counts, total: entry.total });
        currentDate.setMonth(currentDate.getMonth() + 1);
    }
    return out;
};

const Home = () => {
    const navigate = useNavigate();
    const user = JSON.parse(localStorage.getItem('user')) || {};
    const displayName = user.name || user.username || 'Admin';
    const [systemStatus, setSystemStatus] = useState('checking');
    const [currentTime, setCurrentTime] = useState(new Date());

    // Filter state for pie charts (just like in the status)
    const [dailyChartFilter, setDailyChartFilter] = useState('ALL');
    const [monthlyChartFilter, setMonthlyChartFilter] = useState('ALL');

    const [stats, setStats] = useState({
        patients: 0,
        doctors: 0,
        totalRefills: 0,
        overdue: 0,
        dueToday: 0,
        future: 0,
        priorityItems: [],
        expiringItems: [],
        progress: { daily: 0, monthly: 0, dailyGoal: 100, monthlyGoal: 3000, dailyBreakdown: [], dailyByHour: [], monthlyBreakdown: [], monthlyByMonth: [] }
    });

    // API_URL is now managed in src/config.js
    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        const loadDashboardData = async () => {
            try {
                const [patientsRes, doctorsRes, refillsRes, notificationsRes, logsRes, progressRes] = await Promise.all([
                    axios.get(`${API_URL}?action=get_patients`),
                    axios.get(`${API_URL}?action=get_doctors`),
                    axios.get(`${API_URL}?action=get_refills`),
                    axios.get(`${API_URL}?action=get_notifications`),
                    axios.get(`${API_URL}?action=get_logs`),
                    axios.get(`${API_URL}?action=get_progress_stats`)
                ]);

                const today = new Date();
                today.setHours(0, 0, 0, 0);

                let overdue = 0;
                let dueToday = 0;
                let future = 0;

                const refills = Array.isArray(refillsRes.data) ? refillsRes.data : [];

                // Logic for Priority List
                const priorityList = refills.filter(item => {
                    const expDate = new Date(item.expiration_date);
                    expDate.setHours(0, 0, 0, 0);

                    if (expDate < today) {
                        overdue++;
                        return true;
                    }
                    if (expDate.getTime() === today.getTime()) {
                        dueToday++;
                        return true;
                    }
                    return false;
                }).slice(0, 10);

                setStats({
                    patients: Array.isArray(patientsRes.data) ? patientsRes.data.length : 0,
                    doctors: Array.isArray(doctorsRes.data) ? doctorsRes.data.length : 0,
                    totalRefills: refills.length,
                    overdue,
                    dueToday,
                    future,
                    priorityItems: priorityList,
                    expiringItems: Array.isArray(notificationsRes.data) ? notificationsRes.data.slice(0, 10) : [],
                    recentActivity: Array.isArray(logsRes.data) ? logsRes.data.slice(0, 15) : [],
                    progress: progressRes.data || { daily: 0, monthly: 0, dailyGoal: 100, monthlyGoal: 3000 }
                });

                setSystemStatus('online');
            } catch (error) {
                console.error("Dashboard Load Error:", error);
                setSystemStatus('offline');
            }
        };

        loadDashboardData();
    }, []);

    const exportDailyProgress = async () => {
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet('Daily Progress');
        ws.columns = [{ width: 32 }, { width: 14 }];

        // Header row
        const head = ws.addRow(['STATUS', 'TOTAL']);
        head.eachCell((c, col) => {
            styleCellFill(c, HEADER_FILL, false, true);
            c.alignment = { vertical: 'middle', horizontal: col === 1 ? 'left' : 'right' };
        });

        // Status rows
        STATUS_ROWS.forEach(({ key, label, fill, dark }) => {
            const row = ws.addRow([label, Number((stats.progress.dailyBreakdown.find(d => (d.status || '').toUpperCase() === key) || {}).count) || 0]);
            row.eachCell((c, col) => {
                styleCellFill(c, fill, dark, true);
                c.alignment = { vertical: 'middle', horizontal: col === 1 ? 'left' : 'right' };
            });
        });

        // Total row
        const totalCount = stats.progress.dailyBreakdown.reduce((s, d) => s + (Number(d.count) || 0), 0);
        const totalRow = ws.addRow(['TOTAL', totalCount]);
        totalRow.eachCell((c, col) => {
            styleCellFill(c, TOTAL_FILL, false, true);
            c.alignment = { vertical: 'middle', horizontal: col === 1 ? 'left' : 'right' };
        });

        const stamp = new Date().toISOString().slice(0, 10);
        await downloadWorkbook(wb, `Daily_Progress_${stamp}.xlsx`);
    };

    const exportMonthlyProgress = async () => {
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet('Monthly Progress');
        const monthlyRows = buildMonthlyRows(stats.progress.monthlyByMonth);

        ws.columns = [
            { width: 14 }, { width: 8 },
            ...STATUS_ROWS.map(() => ({ width: 16 })),
            { width: 10 }
        ];

        // Header row: Month | Year | <statuses...> | Total
        const headerCells = ['MONTH', 'YEAR', ...STATUS_ROWS.map(s => s.label), 'TOTAL'];
        const headerRow = ws.addRow(headerCells);
        headerRow.eachCell((c, col) => {
            if (col === 1 || col === 2 || col === headerCells.length) {
                styleCellFill(c, NEUTRAL_FILL, false, true);
            } else {
                const sr = STATUS_ROWS[col - 3];
                styleCellFill(c, sr.fill, !!sr.dark, true);
            }
            c.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        });
        headerRow.height = 30;

        // Body
        monthlyRows.forEach(r => {
            const rowVals = [r.monthName, r.year, ...STATUS_ROWS.map(({ key }) => r.counts[key] || 0), r.total];
            const row = ws.addRow(rowVals);
            row.eachCell((c, col) => {
                if (col === 1 || col === 2) {
                    styleCellFill(c, 'FFFFFFFF', false, col === 1);
                    c.alignment = { vertical: 'middle', horizontal: 'left' };
                } else if (col === headerCells.length) {
                    styleCellFill(c, NEUTRAL_FILL, false, true);
                    c.alignment = { vertical: 'middle', horizontal: 'center' };
                } else {
                    styleCellFill(c, 'FFFFFFFF', false, false);
                    c.alignment = { vertical: 'middle', horizontal: 'center' };
                }
            });
        });

        ws.views = [{ state: 'frozen', ySplit: 1 }];

        const stamp = new Date().toISOString().slice(0, 10);
        await downloadWorkbook(wb, `Monthly_Progress_${stamp}.xlsx`);
    };

    const getGreeting = () => {
        const hour = currentTime.getHours();
        if (hour < 12) return "Good Morning";
        if (hour < 18) return "Good Afternoon";
        return "Good Evening";
    };

    return (
        <div className="flex flex-col gap-4 pb-4">

            {/* 1. Header Banner (Green Theme) */}
            <div className="bg-gradient-to-r from-green-800 to-teal-600 rounded-xl p-5 text-white shadow-md relative overflow-hidden shrink-0">
                <div className="absolute top-0 right-0 -mr-10 -mt-10 w-40 h-40 bg-white opacity-10 rounded-full"></div>
                <div className="flex justify-between items-end relative z-10">
                    <div>
                        <p className="text-green-100 text-[10px] font-bold uppercase tracking-wide mb-1 flex items-center gap-2">
                            <Clock size={12} /> {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} • {currentTime.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                        </p>
                        <h1 className="text-xl font-bold">{getGreeting()}, {displayName}</h1>
                    </div>
                    <div className={`px-2 py-1 rounded backdrop-blur-md border flex items-center gap-2 shadow-sm ${systemStatus === 'online' ? 'bg-emerald-500/20 border-emerald-400/30 text-emerald-50' : 'bg-red-500/20 border-red-400/30 text-red-50'}`}>
                        <div className="relative">
                            <div className={`h-2 w-2 rounded-full ${systemStatus === 'online' ? 'bg-emerald-400' : 'bg-red-400'}`}></div>
                            <div className={`absolute top-0 left-0 h-2 w-2 rounded-full animate-ping ${systemStatus === 'online' ? 'bg-emerald-400' : 'bg-red-400'}`}></div>
                        </div>
                        <span className="text-[10px] font-bold uppercase">{systemStatus === 'online' ? 'System Online' : 'Offline'}</span>
                    </div>
                </div>
            </div>

            {/* 2. Stats Grid (Patients + Doctors) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 shrink-0">

                {/* Patients */}
                <div onClick={() => navigate('/patients')} className="relative bg-white p-5 rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg hover:-translate-y-0.5 transition-all cursor-pointer flex justify-between items-center group overflow-hidden">
                    <div className="absolute -right-8 -top-8 w-32 h-32 bg-gradient-to-br from-green-100 to-emerald-50 rounded-full opacity-60 group-hover:scale-125 transition-transform duration-500"></div>
                    <div className="relative z-10">
                        <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest mb-1">Patients</p>
                        <h3 className="text-3xl font-extrabold text-gray-800 tracking-tight">{stats.patients}</h3>
                        <p className="text-[10px] text-emerald-600 font-semibold mt-1 flex items-center gap-1"><TrendingUp size={10} /> Registered patients</p>
                    </div>
                    <div className="relative z-10 p-3 bg-gradient-to-br from-emerald-500 to-green-600 text-white rounded-xl shadow-md group-hover:scale-110 transition"><Users size={22} /></div>
                </div>

                {/* Doctors */}
                <div onClick={() => navigate('/doctors')} className="relative bg-white p-5 rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg hover:-translate-y-0.5 transition-all cursor-pointer flex justify-between items-center group overflow-hidden">
                    <div className="absolute -right-8 -top-8 w-32 h-32 bg-gradient-to-br from-violet-100 to-purple-50 rounded-full opacity-60 group-hover:scale-125 transition-transform duration-500"></div>
                    <div className="relative z-10">
                        <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest mb-1">Doctors</p>
                        <h3 className="text-3xl font-extrabold text-gray-800 tracking-tight">{stats.doctors}</h3>
                        <p className="text-[10px] text-violet-600 font-semibold mt-1 flex items-center gap-1"><TrendingUp size={10} /> Active providers</p>
                    </div>
                    <div className="relative z-10 p-3 bg-gradient-to-br from-violet-500 to-purple-600 text-white rounded-xl shadow-md group-hover:scale-110 transition"><Stethoscope size={22} /></div>
                </div>
            </div>

            {/* NEW: Progress Dashboard - Spreadsheet-style tables */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 shrink-0">
                {/* Daily Progress (Status × Total) */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                        <Activity size={14} className="text-teal-600" />
                        <h3 className="font-bold text-gray-700 text-xs uppercase tracking-wider italic">Daily Progress</h3>
                        <button
                            onClick={exportDailyProgress}
                            className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 rounded-md shadow-sm transition"
                            title="Export Daily Progress to Excel"
                        >
                            <Download size={11} /> Export
                        </button>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="bg-green-200 text-gray-800">
                                    <th className="px-3 py-1.5 text-left font-bold uppercase tracking-wide border border-gray-300">Status</th>
                                    <th className="px-3 py-1.5 text-right font-bold uppercase tracking-wide border border-gray-300">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {STATUS_ROWS.map(({ key, label, bg, text }) => {
                                    const row = stats.progress.dailyBreakdown.find(d => (d.status || '').toUpperCase() === key);
                                    const count = row ? Number(row.count) : 0;
                                    return (
                                        <tr key={key} className={`${bg} ${text}`}>
                                            <td className={`px-3 py-1.5 font-semibold border border-gray-300 ${text}`}>{label}</td>
                                            <td className={`px-3 py-1.5 text-right border border-gray-300 ${text}`}>{count}</td>
                                        </tr>
                                    );
                                })}
                                <tr className="bg-white">
                                    <td className="px-3 py-1.5 font-bold text-gray-800 border border-gray-300">TOTAL</td>
                                    <td className="px-3 py-1.5 text-right font-extrabold text-gray-900 border border-gray-300">
                                        {stats.progress.dailyBreakdown.reduce((s, d) => s + (Number(d.count) || 0), 0)}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Monthly Progress (Month × Status grid) */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden lg:col-span-4">
                    <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                        <Calendar size={14} className="text-indigo-600" />
                        <h3 className="font-bold text-gray-700 text-xs uppercase tracking-wider italic">Monthly Progress</h3>
                        <button
                            onClick={exportMonthlyProgress}
                            className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide bg-indigo-600 hover:bg-indigo-700 text-white px-2 py-1 rounded-md shadow-sm transition"
                            title="Export Monthly Progress to Excel"
                        >
                            <Download size={11} /> Export
                        </button>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-[11px]">
                            <thead>
                                <tr className="text-gray-800">
                                    <th className="px-2 py-1.5 text-left font-bold uppercase border border-gray-300 bg-gray-100">Month</th>
                                    <th className="px-2 py-1.5 text-left font-bold uppercase border border-gray-300 bg-gray-100">Year</th>
                                    {STATUS_ROWS.map(({ key, label, headBg }) => (
                                        <th key={key} className={`px-2 py-1.5 text-center font-bold uppercase border border-gray-300 ${headBg}`}>{label}</th>
                                    ))}
                                    <th className="px-2 py-1.5 text-center font-bold uppercase border border-gray-300 bg-gray-100">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {buildMonthlyRows(stats.progress.monthlyByMonth).map((r) => (
                                    <tr key={r.ym} className="hover:bg-gray-50">
                                        <td className="px-2 py-1.5 font-semibold text-gray-800 border border-gray-300">{r.monthName}</td>
                                        <td className="px-2 py-1.5 text-gray-700 border border-gray-300">{r.year}</td>
                                        {STATUS_ROWS.map(({ key }) => (
                                            <td key={key} className="px-2 py-1.5 text-center text-gray-800 border border-gray-300">{r.counts[key] || 0}</td>
                                        ))}
                                        <td className="px-2 py-1.5 text-center font-bold text-gray-900 border border-gray-300">{r.total}</td>
                                    </tr>
                                ))}
                                {stats.progress.monthlyByMonth && stats.progress.monthlyByMonth.length === 0 && (
                                    <tr><td colSpan={STATUS_ROWS.length + 3} className="text-center py-4 text-gray-400 italic">No monthly data.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* 3. Bottom Section: Breakdown Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 h-[420px]">

                {/* Col 1: Daily Call Breakdown (Bar Chart) */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col overflow-hidden">
                    <div className="p-3 border-b border-gray-50 bg-gray-50/30 flex justify-between items-center shrink-0">
                        <h3 className="font-bold text-gray-700 text-xs uppercase tracking-wider flex items-center gap-2">
                            <Activity size={14} className="text-teal-500" /> Daily Breakdown
                        </h3>
                        <div className="relative">
                            <select
                                value={dailyChartFilter}
                                onChange={(e) => setDailyChartFilter(e.target.value)}
                                className="text-[11px] bg-white border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-teal-500 font-semibold text-gray-600 cursor-pointer shadow-sm"
                            >
                                <option value="ALL">All Statuses</option>
                                {STATUS_ROWS.map(({ key, label }) => (
                                    <option key={key} value={key}>{label}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="flex-1 p-3 flex justify-center items-center min-h-[260px] relative">
                        {(() => {
                            // Fixed business hours: 9 AM - 5 PM US Eastern
                            const HOURS = [9, 10, 11, 12, 13, 14, 15, 16, 17];
                            const formatHour = (h) => {
                                const period = h >= 12 ? 'PM' : 'AM';
                                const display = h % 12 === 0 ? 12 : h % 12;
                                return `${display} ${period}`;
                            };
                            const filterKey = (dailyChartFilter || 'ALL').toUpperCase();
                            const rawRows = stats.progress.dailyByHour || [];
                            const hourlyData = HOURS.map(h => {
                                const matched = rawRows.filter(r =>
                                    Number(r.hour) === h &&
                                    (filterKey === 'ALL' || (r.status || '').toUpperCase() === filterKey)
                                );
                                return {
                                    hour: h,
                                    label: formatHour(h),
                                    count: matched.reduce((s, r) => s + Number(r.count || 0), 0)
                                };
                            });
                            const barFill = filterKey === 'ALL'
                                ? '#14b8a6'
                                : (STATUS_COLORS[filterKey] || STATUS_COLORS['OTHERS']);
                            return (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        data={hourlyData}
                                        margin={{ top: 20, right: 30, left: 0, bottom: 20 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                        <XAxis
                                            dataKey="label"
                                            axisLine={false}
                                            tickLine={false}
                                            tick={{ fontSize: 10, fill: '#64748b' }}
                                            interval={0}
                                        />
                                        <YAxis
                                            axisLine={false}
                                            tickLine={false}
                                            tick={{ fontSize: 10, fill: '#64748b' }}
                                            allowDecimals={false}
                                        />
                                        <Tooltip
                                            cursor={{ fill: '#f8fafc' }}
                                            contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', fontSize: '12px' }}
                                            formatter={(value) => [value, 'Calls']}
                                            labelFormatter={(label) => `${label} EST`}
                                        />
                                        <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={50} fill={barFill} />
                                    </BarChart>
                                </ResponsiveContainer>
                            );
                        })()}
                    </div>
                </div>

                {/* Col 2: Monthly Trend (Stacked Bar Chart) */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col overflow-hidden">
                    <div className="p-3 border-b border-gray-50 bg-gray-50/30 flex justify-between items-center shrink-0">
                        <h3 className="font-bold text-gray-700 text-xs uppercase tracking-wider flex items-center gap-2">
                            <Calendar size={14} className="text-indigo-500" /> Monthly Trend
                        </h3>
                        <div className="relative">
                            <select
                                value={monthlyChartFilter}
                                onChange={(e) => setMonthlyChartFilter(e.target.value)}
                                className="text-[11px] bg-white border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-semibold text-gray-600 cursor-pointer shadow-sm"
                            >
                                <option value="ALL">All Statuses</option>
                                {STATUS_ROWS.map(({ key, label }) => (
                                    <option key={key} value={key}>{label}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="flex-1 p-3 flex justify-center items-center min-h-[260px] relative">
                        {(!stats.progress.monthlyByMonth || stats.progress.monthlyByMonth.length === 0) ? (
                            <p className="text-gray-400 text-xs italic">No monthly calls logged.</p>
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart 
                                    data={buildMonthlyRows(stats.progress.monthlyByMonth).map(r => ({
                                        name: `${r.monthName.substring(0,3)} ${r.year}`,
                                        ...r.counts
                                    }))} 
                                    margin={{ top: 20, right: 10, left: -20, bottom: 0 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fontSize: 10, fill: '#64748b'}} />
                                    <YAxis axisLine={false} tickLine={false} tick={{fontSize: 10, fill: '#64748b'}} allowDecimals={false} />
                                    <Tooltip 
                                        cursor={{fill: '#f8fafc'}} 
                                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', fontSize: '12px' }} 
                                    />
                                    <Legend wrapperStyle={{ fontSize: '9px', paddingTop: '10px' }} />
                                    {STATUS_ROWS.filter(({key}) => monthlyChartFilter === 'ALL' || key === monthlyChartFilter).map(({key}) => (
                                        <Bar key={key} dataKey={key} stackId="a" fill={STATUS_COLORS[key]} radius={4} />
                                    ))}
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default Home;